import type { DebugOption } from "@v2/features/debug/debugOptions";

import * as React from "react";

import { SelectionItem } from "@replohq/lattice/blocks/selection/SelectionItem";
import { Button } from "@replohq/lattice/components/button/Button";
import { Input } from "@replohq/lattice/components/input/Input";
import { Tag } from "@replohq/lattice/components/tag/Tag";
import { toast } from "@replohq/lattice/components/toast/Toast";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import { AgentLaunchModal } from "@v2/features/agent-beta/AgentLaunchModal";
import { DEBUG_OPTIONS } from "@v2/features/debug/debugOptions";
import {
  applyDebugOptionStorageEffect,
  getDebugOptionStorageEffect,
} from "@v2/features/debug/debugOptionStorageEffects";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { agentBetaRouter, agentBetaUtils } from "@v2/trpc/routes/agentBeta";
import { projectRouter, projectUtils } from "@v2/trpc/routes/project";
import { workspaceUtils } from "@v2/trpc/routes/workspace";
import { ArrowRight, Search } from "lucide-react";
import { z } from "zod";

const ToggleOption: React.FC<{ option: DebugOption }> = ({ option }) => {
  const [value, setValue] = useLocalStorageState(option.key, false, {
    schema: z.boolean(),
  });
  return (
    <SelectionItem
      id={`debug-option-${option.key}`}
      type="switch"
      variant="outline"
      label={option.label}
      description={option.description}
      checked={value ?? false}
      onCheckedChange={() => {
        const nextValue = !value;
        setValue(nextValue);
        applyDebugOptionStorageEffect(
          getDebugOptionStorageEffect({ option, nextValue }),
        );
      }}
      UNSAFE_className="p-2 [&_[data-slot=field-label]]:text-xs [&_[data-slot=field-description]]:text-xs [&_[data-slot=field-description]]:[overflow-wrap:anywhere]"
    />
  );
};

const ButtonOption: React.FC<{
  option: DebugOption & { type: "button" };
}> = ({ option }) => {
  const [value, setValue] = useLocalStorageState(option.key, false, {
    schema: z.boolean(),
  });
  const isToggleable = option.isToggleable ?? false;

  let buttonLabel: string;
  if (isToggleable) {
    buttonLabel = value ? "Hide" : "Show";
  } else {
    buttonLabel = option.buttonLabel ?? "Open";
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-border p-2">
      <div className="min-w-0 basis-64 grow">
        <div className="text-xs font-medium">{option.label}</div>
        <div className="text-xs text-muted">{option.description}</div>
      </div>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          const nextValue = isToggleable ? !value : true;
          setValue(nextValue);
          applyDebugOptionStorageEffect(
            getDebugOptionStorageEffect({ option, nextValue }),
          );
        }}
      >
        {buttonLabel}
      </Button>
    </div>
  );
};

const SelectOption: React.FC<{
  option: DebugOption & { type: "select" };
}> = ({ option }) => {
  const [value, setValue] = useLocalStorageState(
    option.key,
    option.defaultValue ?? "",
    { schema: z.string() },
  );

  return (
    <div className="flex flex-col gap-1 rounded border border-border p-2">
      <div className="text-xs font-medium">{option.label}</div>
      <div className="text-xs text-muted">{option.description}</div>
      <select
        className="rounded border border-border bg-background px-2 py-1 text-xs"
        value={value ?? ""}
        onChange={(event) => {
          const nextValue = event.target.value;
          setValue(nextValue);
          applyDebugOptionStorageEffect(
            getDebugOptionStorageEffect({ option, nextValue }),
          );
        }}
      >
        {option.options.map((selectOption) => (
          <option key={selectOption.value} value={selectOption.value}>
            {selectOption.label}
          </option>
        ))}
      </select>
    </div>
  );
};

const TextOption: React.FC<{
  option: DebugOption & { type: "text" };
}> = ({ option }) => {
  const [value, setValue] = useLocalStorageState(
    option.key,
    option.defaultValue ?? "",
    { schema: z.string() },
  );
  const [parentEnabled] = useLocalStorageState(option.showWhen ?? "", false, {
    schema: z.boolean(),
  });

  if (option.showWhen && !parentEnabled) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1 rounded border border-border p-2">
      <div className="text-xs font-medium">{option.label}</div>
      <div className="text-xs text-muted">{option.description}</div>
      <Input
        size="sm"
        placeholder={option.placeholder}
        value={value ?? ""}
        onChange={(event) => setValue(event.target.value)}
      />
    </div>
  );
};

const AgentLaunchModalOptions: React.FC = () => {
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-border p-2">
        <div className="flex min-w-0 basis-64 grow flex-col gap-0.5">
          <div className="text-xs font-medium">Preview Agent launch modal</div>
          <div className="text-xs text-muted">
            Force-opens the agent introduction letter, ignoring every gate and
            dismissal — closing it records nothing.
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setIsPreviewOpen(true)}
        >
          Open
        </Button>
      </div>
      <AgentLaunchModal
        open={isPreviewOpen}
        source="debug-preview"
        onEnterBeta={() => {
          setIsPreviewOpen(false);
          toast({ title: "Preview only — no project created" });
        }}
        onDecline={() => setIsPreviewOpen(false)}
      />
    </>
  );
};

const CreateLegacyV2ProjectOption: React.FC = () => {
  const workspace = useCurrentWorkspace();
  const createProject = projectRouter.create.useMutation({
    onSuccess: async () => {
      await Promise.all([
        projectUtils.listWithStats.invalidate(),
        projectUtils.find.invalidate(),
        projectUtils.listGroupedByWorkspaceForUser.invalidate(),
      ]);
    },
  });

  if (!workspace) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-border p-2">
      <div className="flex min-w-0 basis-64 grow flex-col gap-0.5">
        <div className="text-xs font-medium">Create Legacy V2 Project</div>
        <div className="text-xs text-muted">
          Creates a non-agent, Replo-hosted (Replo Shops) project in this
          workspace for exercising the V2 deprecation migration path. The
          creation backstop is only bypassed for superusers — enforced
          server-side, not by this button.
        </div>
      </div>
      <Button
        variant="secondary"
        size="sm"
        disabled={createProject.isPending}
        onClick={() => {
          const suffix = Math.random().toString(36).slice(2, 8);
          createProject.mutate(
            {
              name: `Legacy V2 Debug ${suffix}`,
              workspaceId: workspace.id,
              hostingType: "replo",
              subdomain: `legacy-v2-debug-${suffix}`,
              isAgentEnabled: false,
              allowLegacyV2ForTesting: true,
            },
            {
              onSuccess: (result) => {
                toast.success({
                  title: "Legacy V2 project created",
                  description: result.project.name ?? result.project.id,
                });
              },
              onError: (error) => {
                toast.error({
                  title: "Failed to create legacy V2 project",
                  description:
                    error instanceof Error ? error.message : "Unknown error",
                });
              },
            },
          );
        }}
      >
        Create
      </Button>
    </div>
  );
};

const AgentBetaStateOptions: React.FC = () => {
  const workspace = useCurrentWorkspace();
  const { data, isLoading } = agentBetaRouter.getStatus.useQuery(
    workspace ? { workspaceId: workspace.id } : skipToken,
  );
  const setRequested = agentBetaRouter.setRequested.useMutation({
    onSuccess: async () => {
      if (workspace) {
        await agentBetaUtils.getStatus.invalidate({
          workspaceId: workspace.id,
        });
      }
    },
  });
  const setEnabled = agentBetaRouter.setEnabled.useMutation({
    onSuccess: async () => {
      if (workspace) {
        await Promise.all([
          agentBetaUtils.getStatus.invalidate({ workspaceId: workspace.id }),
          workspaceUtils.getById.invalidate({ id: workspace.id }),
          workspaceUtils.getUserWorkspacesList.invalidate(),
        ]);
      }
    },
  });

  if (!workspace) {
    return null;
  }

  const isEnabled = Boolean(data?.agentBetaEnabledAt);
  const isRequested = Boolean(data?.agentBetaRequestedAt);
  const initialDefaultLabel =
    workspace.initialDefaultProjectType === "agent"
      ? "Agent"
      : "Shopify Page Builder";
  const currentDefaultLabel = isEnabled ? "Agent" : "Shopify Page Builder";

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-border p-2">
        <div className="flex min-w-0 basis-64 grow flex-col gap-0.5">
          <div className="text-xs font-medium">Default Project Type</div>
          <div className="text-xs text-muted">
            What this workspace opted into at signup (frozen at creation) versus
            what new projects currently open in.
          </div>
        </div>
        <div className="flex max-w-full shrink-0 flex-wrap items-center gap-1.5">
          <Tag variant="neutral" size="base">
            {initialDefaultLabel}
          </Tag>
          <ArrowRight className="size-3.5 shrink-0 text-muted" />
          <Tag variant="success" size="base">
            {currentDefaultLabel}
          </Tag>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-border p-2">
        <div className="flex min-w-0 basis-64 grow flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium">
              {isEnabled ? "Disable Agent" : "Enable Agent"}
            </span>
            <Tag variant="beta" size="sm">
              Beta
            </Tag>
          </div>
          <div className="text-xs text-muted">
            {isEnabled
              ? "New projects in this workspace open in the new Replo builder by default. Turn off to send new projects back to the Shopify Page Builder."
              : "New projects currently open in the Shopify Page Builder. Turn on to make the new Replo builder the default for new projects."}
          </div>
        </div>
        <Button
          variant={isEnabled ? "secondary" : "primary"}
          size="sm"
          disabled={isLoading || setEnabled.isPending}
          onClick={() => {
            setEnabled.mutate({
              workspaceId: workspace.id,
              enabled: !isEnabled,
            });
          }}
        >
          {isEnabled ? "Turn Agent Off" : "Turn Agent On"}
        </Button>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-border p-2">
        <div className="flex min-w-0 basis-64 grow flex-col gap-0.5">
          <div className="text-xs font-medium">
            {isRequested
              ? "Remove Workspace from Agent Waitlist"
              : "Add Workspace to Agent Waitlist"}
          </div>
          <div className="text-xs text-muted">
            {isRequested
              ? "This workspace has requested agent access. Leaving the waitlist removes that request — it doesn't change what new projects open in."
              : "Request agent access for this workspace. Joining the waitlist doesn't change what new projects open in."}
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          disabled={isLoading || setRequested.isPending}
          onClick={() => {
            setRequested.mutate({
              workspaceId: workspace.id,
              requested: !isRequested,
            });
          }}
        >
          {isRequested ? "Leave Waitlist" : "Join Waitlist"}
        </Button>
      </div>
    </>
  );
};

export const OptionsTab: React.FC = () => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const normalizedSearchTerm = searchTerm.toLowerCase();
  const filteredOptions = DEBUG_OPTIONS.filter((option) => {
    return (
      option.label.toLowerCase().includes(normalizedSearchTerm) ||
      option.description.toLowerCase().includes(normalizedSearchTerm)
    );
  });
  const showAgentBetaOptions =
    "early access launch modal letter storage clear dismissal request waitlist enabled disabled access default project type workspace agent beta shopify theme builder remove legacy v2 replo shops sunset deprecation migration create".includes(
      normalizedSearchTerm,
    );

  return (
    <div className="flex flex-col gap-2">
      <Input
        size="sm"
        placeholder="Search options..."
        value={searchTerm}
        autoFocus
        onChange={(event) => setSearchTerm(event.target.value)}
        endEnhancer={<Search className="size-3.5 text-muted" />}
      />
      <div className="flex flex-col gap-1">
        {showAgentBetaOptions && <AgentBetaStateOptions />}
        {showAgentBetaOptions && <AgentLaunchModalOptions />}
        {showAgentBetaOptions && <CreateLegacyV2ProjectOption />}
        {filteredOptions.map((option) => {
          if (option.type === "select") {
            return <SelectOption key={option.key} option={option} />;
          }
          if (option.type === "text") {
            return <TextOption key={option.key} option={option} />;
          }
          if (option.type === "button") {
            return <ButtonOption key={option.key} option={option} />;
          }
          return <ToggleOption key={option.key} option={option} />;
        })}
      </div>
    </div>
  );
};
